/**
 * @file Professionals List Page — /profesionales
 */
import React, { useState, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Search, X, UserX, Stethoscope } from "lucide-react";
import { useProfessionals } from "../hooks/useProfessionals";
import { PROFESSIONAL_STRINGS, COMMON_STRINGS } from "../constants/strings";
import {
  Button,
  Card,
  Chip,
  Input,
  Select,
  SelectItem,
  SelectGroup,
  SelectLabel,
  Spinner,
} from "../ui";
import "./Professionals.css";

export default function Professionals() {
  const { professionals, isLoading } = useProfessionals();
  const [nameQuery, setNameQuery] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("");

  const specialtyGroups = useMemo(() => {
    const names = new Set();
    professionals.forEach((p) => p.specialties?.forEach((s) => names.add(s.name)));
    return PROFESSIONAL_STRINGS.SPECIALTY_GROUPS.map((g) => ({
      group: g.group,
      items: g.items.filter(([, label]) => names.has(label)),
    })).filter((g) => g.items.length > 0);
  }, [professionals]);

  const filtered = useMemo(() => {
    let list = professionals;
    if (nameQuery.trim()) {
      const q = nameQuery.trim().toLowerCase();
      list = list.filter((p) =>
        `${p.user.firstName} ${p.user.lastName}`.toLowerCase().includes(q)
      );
    }
    if (specialtyFilter) {
      list = list.filter((p) => p.specialties?.some((s) => s.name === specialtyFilter));
    }
    return list;
  }, [professionals, nameQuery, specialtyFilter]);

  const clearFilters = () => {
    setNameQuery("");
    setSpecialtyFilter("");
  };

  const hasActiveFilter = nameQuery.trim() || specialtyFilter;

  if (isLoading) {
    return (
      <div className="prof-page">
        <div className="sk-line sk-w-50 sk-h-32" />
        <div className="sk-block sk-h-72" />
        <div className="prof-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="sk-block sk-h-360" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="prof-page">
      <header className="prof-head">
        <span className="prof-eyebrow">Buscar</span>
        <h1 className="prof-title">{PROFESSIONAL_STRINGS.LIST_TITLE}</h1>
      </header>

      <div className="prof-filters">
        <Input
          placeholder={PROFESSIONAL_STRINGS.SEARCH_PLACEHOLDER}
          value={nameQuery}
          onChange={(e) => setNameQuery(e.target.value)}
          leftIcon={<Search size={14} />}
        />
        <Select
          label={PROFESSIONAL_STRINGS.FILTER_SPECIALTY}
          value={specialtyFilter}
          onValueChange={(v) => setSpecialtyFilter(v === "__all__" ? "" : v)}
          placeholder={PROFESSIONAL_STRINGS.FILTER_ALL_SPECIALTIES}
        >
          <SelectItem value="__all__">{PROFESSIONAL_STRINGS.FILTER_ALL_SPECIALTIES}</SelectItem>
          {specialtyGroups.map((g) => (
            <SelectGroup key={g.group}>
              <SelectLabel>{g.group}</SelectLabel>
              {g.items.map(([, label]) => (
                <SelectItem key={label} value={label}>
                  {label}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </Select>
        {hasActiveFilter && (
          <Button variant="ghost" leftIcon={<X size={14} />} onClick={clearFilters}>
            {PROFESSIONAL_STRINGS.CLEAR_FILTERS}
          </Button>
        )}
      </div>

      <p className="prof-count">
        {filtered.length} {filtered.length !== 1 ? COMMON_STRINGS.RESULTS : COMMON_STRINGS.RESULT}
        {hasActiveFilter && ` (de ${professionals.length})`}
      </p>

      {filtered.length === 0 ? (
        <Card className="prof-empty">
          <UserX size={36} className="prof-empty-icon" />
          <p>
            {hasActiveFilter
              ? PROFESSIONAL_STRINGS.LIST_NO_RESULTS
              : PROFESSIONAL_STRINGS.LIST_EMPTY}
          </p>
        </Card>
      ) : (
        <div className="prof-grid">
          {filtered.map((prof) => (
            <Card key={prof.id} hover className="prof-card">
              <h3 className="prof-card-name">
                {prof.user.firstName} {prof.user.lastName}
              </h3>
              {prof.specialties?.length > 0 && (
                <div className="prof-card-chips">
                  {prof.specialties.map((s) => (
                    <Chip key={s.id} tone="primary" size="sm">
                      {s.name}
                    </Chip>
                  ))}
                </div>
              )}
              {prof.bio && (
                <p className="prof-card-bio">
                  {prof.bio.length > 120 ? `${prof.bio.substring(0, 120)}…` : prof.bio}
                </p>
              )}
              {prof.price && (
                <p className="prof-card-price">
                  ${Number(prof.price).toLocaleString("es-MX")} {prof.currency}
                </p>
              )}
              <Button asChild leftIcon={<Stethoscope size={14} />}>
                <RouterLink to={`/profesional/${prof.id}`}>
                  {PROFESSIONAL_STRINGS.VIEW_PROFILE}
                </RouterLink>
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
